import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { API_BASE_URL } from '@mm-aggregator/drf-dynamics';

/**
 * Summary statistics for entities.
 */
export interface EntitySummary {
  merchants: { total: number; active: number };
  applications: { total: number; active: number };
  users: { total: number; active: number };
  wallets: { total: number; with_balance: number };
  entities: { total: number; active: number };
  currencies: { total: number; active: number };
  countries: { total: number; active: number };
}

/**
 * Prior-window metrics for period-over-period comparison (same keys as current, minus today/total).
 */
export interface TransactionPreviousPeriodBucket {
  period: number;
  amount_period_by_currency: Record<string, number>;
  amount_period_currency_meta?: Record<string, { title: string; symbol: string }>;
  count_period_by_currency?: Record<string, number>;
}

/**
 * Previous period window returned with `GET .../dashboard/transactions/`.
 */
export interface TransactionPreviousPeriod {
  period: { start: string; end: string };
  deposits: TransactionPreviousPeriodBucket;
  payouts: TransactionPreviousPeriodBucket;
  refunds: TransactionPreviousPeriodBucket;
  remittances: TransactionPreviousPeriodBucket;
  settlements: TransactionPreviousPeriodBucket;
  batches: TransactionPreviousPeriodBucket;
}

export interface TransactionStatsBucket {
  period: number;
  today: number;
  total: number;
  amount_period_by_currency: Record<string, number>;
  amount_period_currency_meta?: Record<string, { title: string; symbol: string }>;
  count_period_by_currency?: Record<string, number>;
}

/**
 * Transaction statistics.
 */
export interface TransactionStats {
  period: { start: string; end: string };
  deposits: TransactionStatsBucket;
  payouts: TransactionStatsBucket;
  refunds: TransactionStatsBucket;
  remittances: TransactionStatsBucket;
  settlements: TransactionStatsBucket;
  batches: TransactionStatsBucket;
  /** Prior equal-length window before current `period` (counts and amounts for trends). */
  previous_period?: TransactionPreviousPeriod;
}

/**
 * Daily chart data point.
 */
export interface DailyDataPoint {
  date: string;
  label: string;
  deposits_count: number;
  deposits_amount: number;
  payouts_count: number;
  payouts_amount: number;
  refunds_count: number;
  remittances_count: number;
  direct_debit_payments_count: number;
}

/**
 * Chart data response.
 */
export interface DailyChartData {
  daily: DailyDataPoint[];
  days: number;
}

/**
 * Status breakdown item.
 */
export interface StatusBreakdownItem {
  status: string;
  count: number;
}

/**
 * Status breakdown response.
 */
export interface StatusBreakdown {
  deposits: StatusBreakdownItem[];
  payouts: StatusBreakdownItem[];
  refunds: StatusBreakdownItem[];
  remittances: StatusBreakdownItem[];
}

/**
 * Alerts data.
 */
export interface AlertsData {
  deposits: { pending: number; failed: number };
  payouts: { pending: number; failed: number };
  refunds: { pending: number; failed: number };
  remittances: { pending: number; failed: number };
  total_pending: number;
  total_failed: number;
}

/**
 * Recent activity item.
 */
export interface RecentActivityItem {
  id: number;
  internal_reference?: string;
  amount?: number;
  status?: string;
  created_at?: string;
  currency__code?: string;
  application__merchant__name?: string;
  financial_entity__code?: string;
  financial_entity__name?: string;
  financial_entity_logo_url?: string | null;
  transaction_type?: string;
  type_code?: string;
  action?: string;
  entity_type?: string;
  entity_id?: string;
  timestamp?: string;
  user_id?: number;
}

/**
 * Transaction summary stats.
 */
export interface TransactionSummary {
  count: number;
  success_count: number;
  estimated_fees: number;
  total_amount_by_currency?: Record<string, number>;
  success_amount_by_currency?: Record<string, number>;
}

/**
 * Outgoing summary stats (extends base summary with breakdown).
 */
export interface OutgoingSummary extends TransactionSummary {
  payouts: number;
  refunds: number;
  settlements: number;
}

/**
 * Recent activity response.
 */
export interface RecentActivity {
  incoming: RecentActivityItem[];
  outgoing: RecentActivityItem[];
  incoming_summary?: TransactionSummary;
  outgoing_summary?: OutgoingSummary;
  audit_logs: RecentActivityItem[];
  // Legacy fields for backward compatibility
  deposits?: RecentActivityItem[];
  payouts?: RecentActivityItem[];
}

/**
 * Wallet balance item.
 */
export interface WalletBalanceItem {
  currency__code: string;
  total_balance: number;
  wallet_count: number;
}

/**
 * Wallet balances response.
 */
export interface WalletBalances {
  by_currency: WalletBalanceItem[];
  total_wallets: number;
  wallets_with_balance: number;
}

/**
 * Top merchant item.
 */
export interface TopMerchantItem {
  application__merchant__id: number;
  application__merchant__name: string;
  total_amount: number;
  count: number;
}

/**
 * Merchant by country item.
 */
export interface MerchantByCountryItem {
  country__code: string;
  count: number;
}

/**
 * Merchant stats response.
 */
export interface MerchantStats {
  top_by_deposits: TopMerchantItem[];
  top_by_payouts: TopMerchantItem[];
  by_country: MerchantByCountryItem[];
}

export interface TransactionTypeSummary {
  name: string;
  value: number;
  amount?: number;
}

export interface DashboardKpiCard {
  key: string;
  label: string;
  value: number;
  trend_percent: number;
}

export interface DashboardKpiOverview {
  month_period: {
    current_start: string;
    current_end: string;
    previous_start: string;
    previous_end: string;
  };
  kpis: DashboardKpiCard[];
  successful_volume_by_currency: Record<string, number>;
}

/**
 * Dashboard service for fetching BI dashboard data from the backend API.
 */
@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  private get apiUrl(): string {
    return `${this.baseUrl}/auth/dashboard`;
  }

  /**
   * Get overall summary statistics.
   */
  getSummary(): Observable<EntitySummary> {
    return this.http
      .get<EntitySummary>(`${this.apiUrl}/summary/`, { withCredentials: true })
      .pipe(catchError(() => of(this.getEmptySummary())));
  }

  /**
   * Get transaction statistics for a given period.
   */
  getTransactions(period: string = 'today', startDate?: string, endDate?: string): Observable<TransactionStats> {
    let params: Record<string, string> = { period };
    if (period === 'custom' && startDate && endDate) {
      params = { ...params, start_date: startDate, end_date: endDate };
    }
    return this.http
      .get<TransactionStats>(`${this.apiUrl}/transactions/`, { params, withCredentials: true })
      .pipe(catchError(() => of(this.getEmptyTransactionStats())));
  }

  /**
   * Get time-series chart data.
   */
  getCharts(days: number = 30): Observable<DailyChartData> {
    return this.http
      .get<DailyChartData>(`${this.apiUrl}/charts/`, { params: { days: days.toString() }, withCredentials: true })
      .pipe(catchError(() => of({ daily: [], days: 0 })));
  }

  /**
   * Get transaction status breakdown for the same date window as transactions.
   */
  getStatusBreakdown(
    period: string = 'month',
    startDate?: string,
    endDate?: string
  ): Observable<StatusBreakdown> {
    let params: Record<string, string> = { period };
    if (period === 'custom' && startDate && endDate) {
      params = { ...params, start_date: startDate, end_date: endDate };
    }
    return this.http
      .get<StatusBreakdown>(`${this.apiUrl}/status_breakdown/`, { params, withCredentials: true })
      .pipe(catchError(() => of({ deposits: [], payouts: [], refunds: [], remittances: [] })));
  }

  /**
   * Get alerts for pending/failed transactions.
   */
  getAlerts(period?: string, startDate?: string, endDate?: string): Observable<AlertsData> {
    let params: Record<string, string> | undefined;
    if (period) {
      params = { period };
      if (period === 'custom' && startDate && endDate) {
        params = { ...params, start_date: startDate, end_date: endDate };
      }
    }
    return this.http
      .get<AlertsData>(`${this.apiUrl}/alerts/`, { withCredentials: true, params })
      .pipe(catchError(() => of(this.getEmptyAlerts())));
  }

  getKpiOverview(): Observable<DashboardKpiOverview> {
    return this.http
      .get<DashboardKpiOverview>(`${this.apiUrl}/kpi_overview/`, { withCredentials: true })
      .pipe(
        catchError(() =>
          of({
            month_period: {
              current_start: '',
              current_end: '',
              previous_start: '',
              previous_end: '',
            },
            kpis: [],
            successful_volume_by_currency: {},
          })
        )
      );
  }

  /**
   * Get recent activity.
   */
  getRecentActivity(limit: number = 10, period: string = 'today'): Observable<RecentActivity> {
    return this.http
      .get<RecentActivity>(`${this.apiUrl}/recent_activity/`, {
        params: { limit: limit.toString(), period },
        withCredentials: true,
      })
      .pipe(
        catchError(() =>
          of({
            incoming: [],
            outgoing: [],
            incoming_summary: { count: 0, success_count: 0, estimated_fees: 0, total_amount_by_currency: {}, success_amount_by_currency: {} },
            outgoing_summary: {
              count: 0,
              success_count: 0,
              estimated_fees: 0,
              payouts: 0,
              refunds: 0,
              settlements: 0,
              total_amount_by_currency: {},
              success_amount_by_currency: {},
            },
            audit_logs: [],
            deposits: [],
            payouts: [],
          })
        )
      );
  }

  /**
   * Get wallet balance summary.
   */
  getWalletBalances(): Observable<WalletBalances> {
    return this.http
      .get<WalletBalances>(`${this.apiUrl}/wallet_balances/`, { withCredentials: true })
      .pipe(catchError(() => of({ by_currency: [], total_wallets: 0, wallets_with_balance: 0 })));
  }

  /**
   * Get merchant statistics.
   */
  getMerchantStats(): Observable<MerchantStats> {
    return this.http
      .get<MerchantStats>(`${this.apiUrl}/merchant_stats/`, { withCredentials: true })
      .pipe(catchError(() => of({ top_by_deposits: [], top_by_payouts: [], by_country: [] })));
  }

  /**
   * Get transaction type summary for doughnut chart.
   */
  getTransactionTypesSummary(
    period: string = 'today',
    startDate?: string,
    endDate?: string
  ): Observable<TransactionTypeSummary[]> {
    let params: Record<string, string> = { period };
    if (period === 'custom' && startDate && endDate) {
      params = { ...params, start_date: startDate, end_date: endDate };
    }
    return this.http
      .get<TransactionTypeSummary[]>(`${this.baseUrl}/auth/dashboard/transaction_type_summary/`, { params, withCredentials: true })
      .pipe(catchError(() => of([])));
  }

  private getEmptySummary(): EntitySummary {
    return {
      merchants: { total: 0, active: 0 },
      applications: { total: 0, active: 0 },
      users: { total: 0, active: 0 },
      wallets: { total: 0, with_balance: 0 },
      entities: { total: 0, active: 0 },
      currencies: { total: 0, active: 0 },
      countries: { total: 0, active: 0 },
    };
  }

  private getEmptyTransactionStats(): TransactionStats {
    const emptyPrev: TransactionPreviousPeriod = {
      period: { start: '', end: '' },
      deposits: { period: 0, amount_period_by_currency: {} },
      payouts: { period: 0, amount_period_by_currency: {} },
      refunds: { period: 0, amount_period_by_currency: {} },
      remittances: { period: 0, amount_period_by_currency: {} },
      settlements: { period: 0, amount_period_by_currency: {} },
      batches: { period: 0, amount_period_by_currency: {} },
    };
    return {
      period: { start: '', end: '' },
      deposits: { period: 0, today: 0, total: 0, amount_period_by_currency: {} },
      payouts: { period: 0, today: 0, total: 0, amount_period_by_currency: {} },
      refunds: { period: 0, today: 0, total: 0, amount_period_by_currency: {} },
      remittances: { period: 0, today: 0, total: 0, amount_period_by_currency: {} },
      settlements: { period: 0, today: 0, total: 0, amount_period_by_currency: {} },
      batches: { period: 0, today: 0, total: 0, amount_period_by_currency: {} },
      previous_period: emptyPrev,
    };
  }

  private getEmptyAlerts(): AlertsData {
    return {
      deposits: { pending: 0, failed: 0 },
      payouts: { pending: 0, failed: 0 },
      refunds: { pending: 0, failed: 0 },
      remittances: { pending: 0, failed: 0 },
      total_pending: 0,
      total_failed: 0,
    };
  }
}
